import { Button } from "@/shadcn/components/ui/button";
import { Input } from "@/shadcn/components/ui/input";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/shadcn/components/ui/table";
import {
  ColumnDef,
  ColumnFiltersState,
  Table as ReactTable,
  SortingState,
  VisibilityState,
  flexRender,
  getCoreRowModel,
  getFilteredRowModel,
  getPaginationRowModel,
  getSortedRowModel,
  useReactTable,
} from "@tanstack/react-table";
import { useState } from "react";

// Extend ColumnMeta to include getDisplayValue
declare module "@tanstack/react-table" {
  interface ColumnMeta<TData, TValue> {
    getDisplayValue?: (value: TValue, originalRow: TData) => string;
  }
}

import { Checkbox } from "@/shadcn/components/ui/checkbox"; // Import Checkbox component
import {
  DropdownMenu,
  DropdownMenuCheckboxItem,
  DropdownMenuContent,
  DropdownMenuTrigger,
} from "@/shadcn/components/ui/dropdown-menu";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/shadcn/components/ui/popover";
import { ChevronDown, Download, Search } from "lucide-react";
import { useTranslation } from "react-i18next";

interface DataTableProps<TData, TValue> {
  columns: ColumnDef<TData, TValue>[];
  data: TData[];
  includeSelectRow?: boolean;
  disableSearch?: boolean;
  disableColumnSelection?: boolean;
  // export function passing the selected data to the parent component
  exportData?: (data: TData[]) => void;
  // bulk actions function passing the selected data to the parent component
  bulkActions?: (data: TData[]) => void;
  heading?: string;
  headingRef?: string;
  columnFiltersUI?: React.ReactNode; // Accept custom filter UI'
  resetTableFilters?: () => void; // Function to clear table filters
  // Accept initialState for table state (e.g., sorting) so parent can control default sort
  initialState?: {
    sorting?: SortingState;
    // You can add more table state here if needed in the future
  };
  resetFiltersDisabled?: boolean; // Optional prop to control if reset filters button is disabled
}

export function DataTable<TData, TValue>({
  columns,
  data,
  includeSelectRow = false,
  exportData,
  bulkActions,
  disableSearch = false,
  disableColumnSelection = false,
  heading,
  headingRef,
  columnFiltersUI,
  resetTableFilters = () => {}, // Default to empty function if not provided
  initialState, // Accept initialState from parent for default table state
  resetFiltersDisabled = true, // Optional prop to control if reset filters button is disabled
}: DataTableProps<TData, TValue>) {
  "use no memo";
  // TODO: Test memo from react compiler when Tanstack updates

  const { t } = useTranslation();
  // Use initialState.sorting if provided, otherwise default to empty array
  const [sorting, setSorting] = useState<SortingState>(
    initialState?.sorting ?? []
  );
  const [columnFilters, setColumnFilters] = useState<ColumnFiltersState>([]);
  const [columnVisibility, setColumnVisibility] = useState<VisibilityState>({});
  const [rowSelection, setRowSelection] = useState({});
  const [globalFilter, setGlobalFilter] = useState("");

  // Add a selection column if includeSelectRow is true
  const enhancedColumns = includeSelectRow
    ? [
        {
          id: "select",
          header: ({ table }: { table: ReactTable<TData> }) => (
            <Checkbox
              checked={
                table.getIsAllRowsSelected() ||
                (table.getIsSomeRowsSelected() && "indeterminate")
              }
              onCheckedChange={(value) => table.toggleAllRowsSelected(!!value)}
              aria-label="Select all"
            />
          ),

          cell: ({
            row,
          }: {
            row: ReturnType<ReactTable<TData>["getRowModel"]>["rows"][number];
          }) => (
            <Checkbox
              checked={row.getIsSelected()}
              onCheckedChange={(value) => row.toggleSelected(!!value)}
              aria-label="Select row"
            />
          ),
          enableSorting: false,
          enableHiding: false,
        },
        ...columns,
      ]
    : columns;

  const table = useReactTable({
    data,
    columns: enhancedColumns, // Use enhancedColumns instead of columns
    onSortingChange: setSorting,
    onColumnFiltersChange: setColumnFilters,
    getCoreRowModel: getCoreRowModel(),
    getPaginationRowModel: getPaginationRowModel(),
    getSortedRowModel: getSortedRowModel(),
    getFilteredRowModel: getFilteredRowModel(),
    onColumnVisibilityChange: setColumnVisibility,
    onRowSelectionChange: setRowSelection,
    onGlobalFilterChange: setGlobalFilter,
    globalFilterFn: (row, columnId, filterValue) => {
      // Get the raw value
      const rawValue = row.getValue(columnId);

      // Check if the column has a getDisplayValue function defined
      const columnDef = enhancedColumns.find(
        (col) => "accessorKey" in col && col.accessorKey === columnId
      );

      // Use display value if available, otherwise use raw value
      let displayValue = rawValue;
      if (columnDef && "meta" in columnDef && columnDef.meta?.getDisplayValue) {
        displayValue = columnDef.meta.getDisplayValue(
          rawValue as TValue,
          row.original
        );
      }

      // Convert to string for searching
      const valueStr = String(displayValue).toLowerCase();
      const filterStr = String(filterValue).toLowerCase();

      return valueStr.includes(filterStr);
    },

    state: {
      sorting,
      columnFilters,
      columnVisibility,
      rowSelection,
      globalFilter,
    },
  });

  function clearTableFilters() {
    setGlobalFilter("");
    resetTableFilters();
  }

  return (
    <div className="w-full">
      {(heading || headingRef) && (
        <h2 className="mt-4 text-center text-2xl font-bold">
          {heading
            ? `${t(heading ?? "")} ${headingRef ? `: ${t(headingRef)}` : ""}`
            : ""}
        </h2>
      )}
      {/* Render custom column filters UI if provided */}

      <div className="flex flex-col items-center py-2 md:flex-row">
        {(!disableSearch || columnFiltersUI) && (
          <div className="flex flex-wrap items-center gap-4">
            {!disableSearch && (
              <div className="relative max-w-sm">
                <Search className="text-muted-foreground absolute top-1/2 left-2 h-4 w-4 -translate-y-1/2" />
                <Input
                  placeholder={t("Search all columns...")}
                  value={globalFilter}
                  onChange={(event) => setGlobalFilter(event.target.value)}
                  className="max-w-sm pl-8"
                />
              </div>
            )}
            {columnFiltersUI && (
              <div className="flex flex-wrap gap-2">{columnFiltersUI}</div>
            )}
            <Button
              variant="secondary"
              onClick={() => clearTableFilters()}
              className="ml-2"
              disabled={
                !table.getState().columnFilters.length &&
                !globalFilter &&
                resetFiltersDisabled
              }
            >
              {t("Reset Filters")}
            </Button>
          </div>
        )}

        <div className="ml-auto flex items-center space-x-2">
          <div className="flex items-center space-x-2">
            {
              // show bulk actions button if bulkActions is defined and there are selected rows
              bulkActions &&
                table.getFilteredSelectedRowModel().rows.length > 0 && (
                  <Popover>
                    <PopoverTrigger asChild>
                      <Button
                        variant="outline"
                        onClick={() => {
                          bulkActions(
                            //  pass the selected data to the parent component
                            // if no row is selected, export all rows
                            table
                              .getFilteredSelectedRowModel()
                              .rows.map((row) => row.original)
                          );
                        }}
                      >
                        {t("Bulk Actions")} (
                        {table.getFilteredSelectedRowModel().rows.length})
                      </Button>
                    </PopoverTrigger>
                    <PopoverContent className="" side="bottom" align="center">
                      {t("Unavailable: Demo Mode")}
                    </PopoverContent>
                  </Popover>
                )
            }

            {exportData && (
              <Popover>
                <PopoverTrigger asChild>
                  <Button
                    variant="outline"
                    className="ml-auto"
                    onClick={() => {
                      exportData(
                        //  pass the selected data to the parent component
                        // if no row is selected, export all rows
                        table.getFilteredSelectedRowModel().rows.length
                          ? table
                              .getFilteredSelectedRowModel()
                              .rows.map((row) => row.original)
                          : table
                              .getFilteredRowModel()
                              .rows.map((row) => row.original)
                      );
                    }}
                  >
                    <Download />
                    {table.getFilteredSelectedRowModel().rows.length
                      ? t("Export Selected")
                      : t("Export All")}
                  </Button>
                </PopoverTrigger>
                <PopoverContent className="" side="bottom" align="center">
                  {t("Unavailable: Demo Mode")}
                </PopoverContent>
              </Popover>
            )}
          </div>
          {!disableColumnSelection && (
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button variant="outline" className="ml-auto">
                  {t("Columns")} <ChevronDown />
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end">
                {/* reset button */}
                <DropdownMenuCheckboxItem
                  className="capitalize"
                  checked={false}
                  onCheckedChange={() => {
                    table.resetColumnVisibility();
                  }}
                >
                  {t("Reset")}
                </DropdownMenuCheckboxItem>

                {table
                  .getAllColumns()
                  .filter((column) => column.getCanHide())
                  .map((column) => {
                    return (
                      <DropdownMenuCheckboxItem
                        key={column.id}
                        className="capitalize"
                        checked={column.getIsVisible()}
                        onCheckedChange={(value) =>
                          column.toggleVisibility(!!value)
                        }
                      >
                        {column.id}
                      </DropdownMenuCheckboxItem>
                    );
                  })}
              </DropdownMenuContent>
            </DropdownMenu>
          )}
        </div>
      </div>
      <div className="rounded-md border">
        <Table>
          <TableHeader className="bg-secondary">
            {table.getHeaderGroups().map((headerGroup) => (
              <TableRow key={headerGroup.id}>
                {headerGroup.headers.map((header) => {
                  // Apply column width styling if size is specified
                  const columnDef = header.column.columnDef;
                  const width = columnDef.size;
                  const style = width
                    ? {
                        width: `${width}px`,
                        minWidth: `${width}px`,
                        // Remove maxWidth to allow content expansion when needed
                      }
                    : {};

                  return (
                    <TableHead
                      key={header.id}
                      style={style}
                      className={width ? "whitespace-normal" : ""}
                    >
                      {header.isPlaceholder
                        ? null
                        : flexRender(
                            header.column.columnDef.header,
                            header.getContext()
                          )}
                    </TableHead>
                  );
                })}
              </TableRow>
            ))}
          </TableHeader>
          <TableBody>
            {table.getRowModel().rows?.length ? (
              table.getRowModel().rows.map((row) => (
                <TableRow
                  key={row.id}
                  data-state={row.getIsSelected() && "selected"}
                >
                  {row.getVisibleCells().map((cell) => {
                    // Apply same width styling to cells as headers
                    const columnDef = cell.column.columnDef;
                    const width = columnDef.size;
                    const style = width
                      ? {
                          width: `${width}px`,
                          minWidth: `${width}px`,
                          // Remove maxWidth to allow content expansion when needed
                        }
                      : {};

                    return (
                      <TableCell
                        key={cell.id}
                        style={style}
                        className={
                          width ? "wrap-anywhere whitespace-normal" : ""
                        }
                      >
                        {flexRender(
                          cell.column.columnDef.cell,
                          cell.getContext()
                        )}
                      </TableCell>
                    );
                  })}
                </TableRow>
              ))
            ) : (
              <TableRow>
                <TableCell
                  colSpan={columns.length}
                  className="h-24 text-center"
                >
                  {t("No data to display")}
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </div>
      <div className="flex items-center justify-end space-x-2 py-2">
        <div className="text-muted-foreground flex-1 text-sm">
          {includeSelectRow && (
            <>
              {table.getFilteredSelectedRowModel().rows.length} of{" "}
              {table.getFilteredRowModel().rows.length} row(s) selected.
            </>
          )}
        </div>
        <div className="space-x-2">
          {
            // if number of rows is more than 10, show pagination buttons
            table.getFilteredRowModel().rows.length > 10 && (
              <div className="flex flex-col items-center space-x-2">
                <div className="flex items-center space-x-2">
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => table.previousPage()}
                    disabled={!table.getCanPreviousPage()}
                  >
                    {t("Previous")}
                  </Button>
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => table.nextPage()}
                    disabled={!table.getCanNextPage()}
                  >
                    {t("Next")}
                  </Button>
                </div>
                {/* page numbers */}
                <div className="flex items-center space-x-2">
                  <span className="text-sm">
                    {t("Page")} {table.getState().pagination.pageIndex + 1}{" "}
                    {t("of")} {table.getPageCount()}
                  </span>
                </div>
              </div>
            )
          }
        </div>
      </div>
    </div>
  );
}
